import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { ClockButton, ChatButton, MorningBriefing, ChangePasswordDialog } from "@/components/PortalWidgets";
import { useAuth } from "@/context/AuthContext";
import { navForRole, ROLE_LABELS } from "@/lib/nav";
import { OHC_LOGO, initials, timeAgo } from "@/components/common";
import { Bell, Search, LogOut, Menu, X, ChevronDown, KeyRound } from "lucide-react";
import api from "@/lib/api";
import FollowupReminders from "@/components/FollowupReminders";
import PhoneBridge from "@/components/PhoneBridge";
import DesktopCallPrompt from "@/components/DesktopCallPrompt";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";

function SidebarInner({ nav, collapsed }) {
  return (
    <div className="flex flex-col h-full bg-[#312E81] text-white">
      <div className={`h-16 flex items-center border-b border-white/10 ${collapsed ? "justify-center px-0" : "gap-2 px-5"}`}>
        <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
          <img src={OHC_LOGO} alt="OhCampus" className="w-8 h-8 object-contain" />
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-[15px] tracking-tight">OhCampus</div>
            <div className="text-[10px] uppercase tracking-wider text-[#818CF8] font-semibold">Admissions CRM</div>
          </div>
        )}
      </div>
      <TooltipProvider delayDuration={100}>
        <nav className={`flex-1 overflow-y-auto py-3 space-y-0.5 ${collapsed ? "px-2" : "px-2"}`}>
          {nav.map((n) => {
            const Icon = n.icon;
            const testid = `nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
            const link = (
              <NavLink key={n.path} to={n.path} end={n.path === "/"} data-testid={testid}
                className={({ isActive }) =>
                  `ohc-sidebar-link flex items-center rounded-lg text-[13.5px] font-medium text-slate-200 ${collapsed ? "justify-center h-11 w-11 mx-auto" : "gap-3 px-3 py-2"} ${isActive ? "active text-white" : ""}`}>
                <Icon className="w-[18px] h-[18px] shrink-0" />
                {!collapsed && <span>{n.label}</span>}
              </NavLink>
            );
            if (!collapsed) return link;
            return (
              <Tooltip key={n.path}>
                <TooltipTrigger asChild>{link}</TooltipTrigger>
                <TooltipContent side="right" className="bg-[#1E1B4B] text-white border-0 font-medium">{n.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </nav>
      </TooltipProvider>
      {!collapsed && <div className="p-3 border-t border-white/10 text-[11px] text-slate-400">© 2026 OhCampus.com</div>}
    </div>
  );
}

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const nav = navForRole(user.role);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [search, setSearch] = useState("");
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const load = () => api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const markAllRead = async () => {
    const items = notifs.filter((n) => !n.read);
    if (!items.length) return;
    setNotifs((ns) => ns.map((n) => ({ ...n, read: true })));
    await Promise.all(items.map((n) => api.post(`/notifications/${n.id}/read`).catch(() => {})));
  };

  const clearNotifs = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setNotifs([]);
    await api.delete("/notifications").catch(() => {});
  };

  const doSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    const onQueue = location.pathname.startsWith("/calling-queue");
    navigate(`${onQueue ? "/calling-queue" : "/leads"}?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Desktop icon rail */}
      <aside className="hidden lg:block w-16 shrink-0 fixed inset-y-0 z-30"><SidebarInner nav={nav} collapsed /></aside>

      {/* Mobile drawer (full labels) */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 h-full"><SidebarInner nav={nav} collapsed={false} /></div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
          <button className="absolute top-4 right-4 text-white" onClick={() => setOpen(false)}><X /></button>
        </div>
      )}

      <div className="flex-1 lg:ml-16 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden text-[#312E81]" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu /></button>
          <form onSubmit={doSearch} className="relative flex-1 max-w-md hidden sm:block">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="global-search-input"
              placeholder="Search students, leads, mobile…"
              className="pl-9 h-10 bg-slate-50 border-slate-200" />
          </form>
          <div className="flex-1" data-testid="header-spacer" />
          <ClockButton />
          <ChatButton />

          <DropdownMenu onOpenChange={(o) => { if (o) markAllRead(); }}>
            <DropdownMenuTrigger asChild>
              <button className="relative w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="notifications-btn">
                <Bell className="w-5 h-5 text-slate-600" />
                {unread > 0 && <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#4F46E5] text-white text-[10px] flex items-center justify-center font-bold">{unread}</span>}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 bg-white">
              <DropdownMenuLabel className="flex items-center justify-between">Notifications
                {notifs.length > 0 && <button onClick={clearNotifs} className="text-xs font-semibold text-rose-600 hover:underline" data-testid="notifications-clear-btn">Clear all</button>}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifs.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-400" data-testid="notifications-empty">No notifications</div>}
              <div className="max-h-[60vh] overflow-y-auto" data-testid="notifications-list">
              {notifs.map((n) => (
                <div key={n.id} onClick={() => { if (n.link?.startsWith("/")) navigate(n.link); if (!n.read) { api.post(`/notifications/${n.id}/read`).catch(() => {}); setNotifs((ns) => ns.map((x) => (x.id === n.id ? { ...x, read: true } : x))); } }}
                  className={`px-3 py-2 hover:bg-slate-50 border-b last:border-0 ${n.link?.startsWith("/") ? "cursor-pointer" : ""}`} data-testid={`notif-${n.id}`}>
                  <div className="text-sm font-semibold text-slate-800">{n.title}</div>
                  <div className="text-xs text-slate-500">{n.message}</div>
                  {n.created_at && <div className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</div>}
                </div>
              ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-100" data-testid="user-menu-btn">
                <div className="w-9 h-9 rounded-lg bg-[#312E81] text-white flex items-center justify-center text-sm font-bold font-heading">{initials(user.name)}</div>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-semibold text-slate-800">{user.name}</div>
                  <div className="text-[11px] text-[#4F46E5] font-semibold">{ROLE_LABELS[user.role]}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-white">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold">{user.name}</div>
                <div className="text-xs text-slate-500 font-normal">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-btn">
                <KeyRound className="w-4 h-4 mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-rose-600">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <FollowupReminders />
        <PhoneBridge />
        <DesktopCallPrompt />

        <main className="flex-1 p-4 sm:p-6 lg:p-8 ohc-fade-up pb-24 lg:pb-8">{children}</main>
        {user.must_change_password ? <ChangePasswordDialog open forced /> : <MorningBriefing />}
        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      </div>
    </div>
  );
}
